import React, { useEffect, useRef, useState } from 'react';
import { logViolation } from '../api';

export default function VoiceDetection({ token, onViolation, hasPermissions = false, onVoiceActivity, onMicMute }) {
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const streamRef = useRef(null);
  const dataArrayRef = useRef(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [violations, setViolations] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const lastViolationTimeRef = useRef(0);
  const silenceFramesRef = useRef(0);
  const micMuteCheckIntervalRef = useRef(null);
  const continuousSpeakingStartRef = useRef(null); // Track when continuous speaking started
  const speakingFramesRef = useRef(0); // Count consecutive frames with voice detected
  const REQUIRED_CONTINUOUS_SPEAKING_DURATION = 2000; // 2 seconds of continuous speaking
  const MIN_SPEAKING_FRAMES = 60; // ~2 seconds at 30fps (60 frames)

  useEffect(() => {
    if (!hasPermissions) {
      // Clean up when permissions are not granted
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      if (micMuteCheckIntervalRef.current) {
        clearInterval(micMuteCheckIntervalRef.current);
        micMuteCheckIntervalRef.current = null;
      }
      setIsDetecting(false);
      return;
    }

    let mounted = true;
    let animationFrameId = null;

    const setupAudio = async () => {
      try {
        // Stop any existing stream before requesting new one
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        if (micMuteCheckIntervalRef.current) {
          clearInterval(micMuteCheckIntervalRef.current);
          micMuteCheckIntervalRef.current = null;
        }
        
        // Get microphone stream
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        if (!mounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        // Create audio context
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Double-check mounted state before proceeding
        if (!mounted) {
          stream.getTracks().forEach(track => track.stop());
          audioContext.close().catch(() => {});
          return;
        }
        
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.3; // Lower smoothing for more responsive detection
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        // Calibration: measure background noise for first 2 seconds
        let calibrationSamples = [];
        let calibrationComplete = false;
        let calibrationStartTime = Date.now();
        const CALIBRATION_DURATION = 2000; // 2 seconds

        // Final mounted check before setting refs
        if (!mounted) {
          stream.getTracks().forEach(track => track.stop());
          microphone.disconnect();
          audioContext.close().catch(() => {});
          return;
        }
        
        analyserRef.current = analyser;
        microphoneRef.current = microphone;
        audioContextRef.current = audioContext;
        streamRef.current = stream;
        dataArrayRef.current = dataArray;

        microphone.connect(analyser);
        setIsDetecting(true);

        // Ensure audio track is enabled and active
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const audioTrack = audioTracks[0];
          if (!audioTrack.enabled) {
            audioTrack.enabled = true;
          }
          // Monitor track state changes
          audioTrack.onended = () => {
            if (onMicMute) onMicMute(true, 'track-ended');
          };
          audioTrack.onmute = () => {
            if (onMicMute) onMicMute(true, 'muted');
          };
          audioTrack.onunmute = () => {
            if (onMicMute) onMicMute(false, null);
          };
        }


        // Check for mic mute state periodically
        const checkMicMute = () => {
          if (!mounted || !streamRef.current) return;
          
          const audioTracks = streamRef.current.getAudioTracks();
          if (audioTracks.length === 0) {
            if (onMicMute) onMicMute(true, 'no-audio-track');
            return;
          }

          const track = audioTracks[0];
          const isMuted = !track.enabled || track.muted || track.readyState === 'ended';
          
          if (isMuted && onMicMute) {
            onMicMute(true, track.readyState === 'ended' ? 'track-ended' : (track.enabled ? 'muted' : 'disabled'));
          } else if (!isMuted && onMicMute) {
            onMicMute(false, null);
          }
          
          // If track is ended, log warning (the effect will re-run if hasPermissions is still true)
          if (track.readyState === 'ended' && mounted) {
          }
        };

        // Check immediately and then every 2 seconds
        checkMicMute();
        micMuteCheckIntervalRef.current = setInterval(checkMicMute, 2000);

        // Start analyzing
        const detectVoice = () => {
          if (!mounted || !analyserRef.current) return;

          analyserRef.current.getByteFrequencyData(dataArrayRef.current);

          // Calculate average volume from frequency data
          // Focus on speech frequency range (300-3400 Hz) to filter out clicks/keyboard
          const sampleRate = audioContextRef.current.sampleRate;
          const nyquist = sampleRate / 2;
          const binWidth = nyquist / dataArrayRef.current.length;
          
          // Speech frequency range: 300-3400 Hz
          const speechMinBin = Math.floor(300 / binWidth);
          const speechMaxBin = Math.floor(3400 / binWidth);
          
          let sum = 0;
          let speechSum = 0;
          let maxFreq = 0;
          let speechMaxFreq = 0;
          let speechBinCount = 0;
          
          for (let i = 0; i < dataArrayRef.current.length; i++) {
            sum += dataArrayRef.current[i];
            if (dataArrayRef.current[i] > maxFreq) {
              maxFreq = dataArrayRef.current[i];
            }
            
            // Focus on speech frequency range
            if (i >= speechMinBin && i <= speechMaxBin) {
              speechSum += dataArrayRef.current[i];
              speechBinCount++;
              if (dataArrayRef.current[i] > speechMaxFreq) {
                speechMaxFreq = dataArrayRef.current[i];
              }
            }
          }
          
          const averageVolume = sum / dataArrayRef.current.length;
          const speechAverageVolume = speechBinCount > 0 ? speechSum / speechBinCount : 0;

          // Also check time domain for speech patterns (better for voice detection)
          const timeData = new Uint8Array(analyserRef.current.fftSize);
          analyserRef.current.getByteTimeDomainData(timeData);
          
          // Calculate RMS (Root Mean Square) for time domain - better for speech detection
          let sumSquares = 0;
          let peak = 0;
          for (let i = 0; i < timeData.length; i++) {
            const normalized = (timeData[i] - 128) / 128;
            const absValue = Math.abs(normalized);
            if (absValue > peak) peak = absValue;
            sumSquares += normalized * normalized;
          }
          const rms = Math.sqrt(sumSquares / timeData.length) * 100;
          const peakLevel = peak * 100;

          const now = Date.now();
          
          // Calibration phase: measure background noise
          if (!calibrationComplete && (now - calibrationStartTime) < CALIBRATION_DURATION) {
            calibrationSamples.push({ rms, averageVolume, peakLevel, speechAverageVolume });
            animationFrameId = requestAnimationFrame(detectVoice);
            return;
          } else if (!calibrationComplete) {
            // Calculate baseline from calibration samples
            const avgRms = calibrationSamples.reduce((sum, s) => sum + s.rms, 0) / calibrationSamples.length;
            const avgVolume = calibrationSamples.reduce((sum, s) => sum + s.averageVolume, 0) / calibrationSamples.length;
            const avgPeak = calibrationSamples.reduce((sum, s) => sum + s.peakLevel, 0) / calibrationSamples.length;
            const avgSpeechVolume = calibrationSamples.reduce((sum, s) => sum + (s.speechAverageVolume || 0), 0) / calibrationSamples.length;
            
            // Set adaptive thresholds based on background noise
            // Voice should be at least 1.8x the background noise (more sensitive to voice)
            const VOICE_RMS_THRESHOLD = Math.max(4, avgRms * 1.8); // More sensitive to voice
            const VOICE_FREQ_THRESHOLD = Math.max(15, avgVolume * 1.8); // More sensitive to voice
            const VOICE_PEAK_THRESHOLD = Math.max(8, avgPeak * 1.8); // More sensitive to voice
            const VOICE_SPEECH_FREQ_THRESHOLD = Math.max(12, avgSpeechVolume * 1.8); // Speech-specific threshold
            
            // Store thresholds in closure
            window.voiceThresholds = { 
              VOICE_RMS_THRESHOLD, 
              VOICE_FREQ_THRESHOLD, 
              VOICE_PEAK_THRESHOLD,
              VOICE_SPEECH_FREQ_THRESHOLD 
            };
            
            calibrationComplete = true;
          }

          // Use calibrated thresholds or defaults (more sensitive for voice detection)
          const thresholds = window.voiceThresholds || {
            VOICE_RMS_THRESHOLD: 4, // More sensitive for better voice detection
            VOICE_FREQ_THRESHOLD: 15, // More sensitive for better voice detection
            VOICE_PEAK_THRESHOLD: 8, // More sensitive for better voice detection
            VOICE_SPEECH_FREQ_THRESHOLD: 12 // Speech-specific threshold
          };

          const timeSinceLastViolation = now - lastViolationTimeRef.current;
          const MIN_VIOLATION_INTERVAL = 2000; // 2 seconds between violations

          // Improved voice detection: simpler logic for better sensitivity
          // Voice is detected if RMS is high (primary indicator of sustained sound)
          // AND at least one other indicator (frequency, peak, or speech frequency)
          // This makes it more sensitive to voice while still filtering brief clicks
          const rmsCheck = rms > thresholds.VOICE_RMS_THRESHOLD && rms > 3; // Lower minimum for better voice detection
          const speechFreqCheck = speechAverageVolume > thresholds.VOICE_SPEECH_FREQ_THRESHOLD && speechAverageVolume > 8; // Even lower minimum
          const freqCheck = averageVolume > thresholds.VOICE_FREQ_THRESHOLD && averageVolume > 10; // Lower minimum
          const peakCheck = peakLevel > thresholds.VOICE_PEAK_THRESHOLD && peakLevel > 5; // Lower minimum
          
          // Voice detected if:
          // - RMS is high (sustained energy) - primary indicator
          // - AND at least one of: speech frequency, overall frequency, or peak
          // This is much less strict - prioritizes RMS which is best for voice
          const voiceDetected = rmsCheck && (speechFreqCheck || freqCheck || peakCheck);

          // Update voice activity state for visual indicator
          setIsSpeaking(voiceDetected);
          setAudioLevel(rms); // Use RMS as audio level indicator
          
          // Notify parent component of voice activity
          if (onVoiceActivity) {
            onVoiceActivity({
              isSpeaking: voiceDetected,
              audioLevel: rms,
              volume: averageVolume
            });
          }

          if (voiceDetected) {
            silenceFramesRef.current = 0;
            speakingFramesRef.current++;

            // Track when continuous speaking started
            if (continuousSpeakingStartRef.current === null) {
              continuousSpeakingStartRef.current = now;
            }

            // Check if user has been speaking continuously for required duration
            const continuousSpeakingDuration = now - continuousSpeakingStartRef.current;
            const hasSpokenLongEnough = continuousSpeakingDuration >= REQUIRED_CONTINUOUS_SPEAKING_DURATION;
            const hasEnoughFrames = speakingFramesRef.current >= MIN_SPEAKING_FRAMES;

            // Only record violation if:
            // 1. User has been speaking continuously for 4+ seconds
            // 2. AND enough time has passed since last violation
            if (hasSpokenLongEnough && hasEnoughFrames && timeSinceLastViolation > MIN_VIOLATION_INTERVAL) {
              recordViolation('voice-detected', { 
                rms: rms.toFixed(2), 
                volume: averageVolume.toFixed(2),
                duration: continuousSpeakingDuration.toFixed(0)
              });
              lastViolationTimeRef.current = now;
              // Reset continuous speaking counter after violation
              continuousSpeakingStartRef.current = null;
              speakingFramesRef.current = 0;
            }
          } else {
            // Reset continuous speaking tracking if silence detected
            // Allow brief pauses (increased from 10 to 20 frames = ~0.6 seconds)
            // This prevents resetting on brief pauses in speech
            silenceFramesRef.current++;
            if (silenceFramesRef.current > 20) { // Reset after ~0.6 seconds of silence
              continuousSpeakingStartRef.current = null;
              speakingFramesRef.current = 0;
            }
          }

          animationFrameId = requestAnimationFrame(detectVoice);
        };

        detectVoice();
      } catch (error) {
        if (onViolation) {
          onViolation('microphone-access-denied', { error: error.message });
        }
      }
    };

    setupAudio();

    return () => {
      mounted = false;
      
      // Reset voice tracking
      continuousSpeakingStartRef.current = null;
      speakingFramesRef.current = 0;
      silenceFramesRef.current = 0;
      
      // Clear mic mute check interval
      if (micMuteCheckIntervalRef.current) {
        clearInterval(micMuteCheckIntervalRef.current);
        micMuteCheckIntervalRef.current = null;
      }
      
      // Cancel animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Disconnect microphone source
      if (microphoneRef.current) {
        try {
          microphoneRef.current.disconnect();
        } catch (err) {
          // Already disconnected, ignore
        }
        microphoneRef.current = null;
      }
      
      // Stop all tracks in the stream
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              track.stop();
            }
          });
        } catch (err) {
          // Stream already stopped, ignore
        }
        streamRef.current = null;
      }
      
      // Close audio context only if it's not already closed
      if (audioContextRef.current) {
        try {
          // Check if AudioContext is already closed
          if (audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(err => {
              // Context might be closing/closed, ignore error
            });
          }
        } catch (err) {
          // Context already closed or in invalid state, ignore
        }
        audioContextRef.current = null;
      }
      
      // Clear refs
      analyserRef.current = null;
      dataArrayRef.current = null;
      
      setIsDetecting(false);
    };
  }, [hasPermissions, onViolation]);

  const recordViolation = (type, meta = {}) => {
    setViolations((v) => v + 1);
    if (onViolation) {
      onViolation(type, meta);
    }
    if (token) {
      logViolation(token, type, meta).catch(() => {});
    }
  };

  return null; // Component doesn't render UI, just handles detection
}

